<template>
  <div :class="{ 'booking-loading': showLoading }">
    <v-progress-circular
      v-if="showLoading"
      :size="70"
      :width="7"
      color="primary"
      indeterminate
      class="centered"
    ></v-progress-circular>
    <h1 class="text-center mt-3">Booking</h1>
    <div class="form shadow p-5 mb-5 bg-white rounded">
      <div class="row">
        <div class="col-4">
          <BrandDropdown
            v-model="form.brandSelected"
            :required="true"
            :setDisable="id != null"
            @setBrand="setBrand"
            @setBrands="setBrands"
            @setLoading="setBrandsLoading"
          ></BrandDropdown>
          <div v-if="isSubmit && error.errorBrand" class="text-error">
            {{ error.errorBrand.message }}
          </div>
        </div>
        <div class="col-4">
          <StoreDropdown
            v-model="form.storeSelected"
            :required="true"
            :setDisable="id != null"
            :brandCode="form.brandSelected ? form.brandSelected.id : null"
            @setStore="setStore"
            @setStores="setStores"
            @setLoading="setStoresLoading"
          ></StoreDropdown>
          <div v-if="isSubmit && error.errorStore" class="text-error">
            {{ error.errorStore.message }}
          </div>
        </div>
        <div class="col-4">
          <label for="storeCodeInput">Store Code</label>
          <input
            id="storeCodeInput"
            v-model="form.storeCode"
            type="text"
            class="form-control"
            style="margin: 0.6rem 0 0"
            readonly
          />
        </div>
      </div>
      <div class="row">
        <div class="col-4">
          <label for="customerMobileInput"
            >Customer Mobile<span class="required">*</span></label
          >
          <input
            id="customerMobileInput"
            v-model="form.customerMobile"
            type="text"
            class="form-control"
            style="margin: 0.6rem 0 0"
            @blur="setCustomerMobile"
            :readonly="isReadonly || isConfirmed || !isCreatedByAdmin"
          />
          <div v-if="isSubmit && error.errorCustomerMobile" class="text-error">
            {{ error.errorCustomerMobile.message }}
          </div>
        </div>
        <div class="col-4">
          <label for="customerNameInput">Customer Name</label>
          <input
            id="customerNameInput"
            v-model="form.customerName"
            type="text"
            class="form-control"
            style="margin: 0.6rem 0 0"
            :readonly="isReadonly || isConfirmed || !isCreatedByAdmin"
          />
          <div v-if="isSubmit && error.errorCustomerName" class="text-error">
            {{ error.errorCustomerName.message }}
          </div>
        </div>
        <div class="col-4">
          <label for="customerCodeInput"
            >Member ID<span class="required">*</span></label
          >
          <input
            id="customerCodeInput"
            v-model="form.customerCode"
            type="text"
            class="form-control"
            style="margin: 0.6rem 0 0"
            readonly
          />
        </div>
        <div class="col-4">
          <ServiceDropdown
            v-model="form.serviceSelected"
            :required="true"
            :storeCode="form.storeSelected ? form.storeSelected.id : null"
            :setDisable="isReadonly || isConfirmed || !isCreatedByAdmin"
            @setService="setService"
            @setServices="setServices"
            @setLoading="setServicesLoading"
          ></ServiceDropdown>
          <div v-if="isSubmit && error.errorService" class="text-error">
            {{ error.errorService.message }}
          </div>
        </div>
        <div class="col-4">
          Date<span class="required">*</span>
          <date-picker
            v-bind="$attrs"
            v-model="form.date"
            format="D MMMM yyyy"
            lang="en"
            :value="form.date"
            :setDisable="isReadonly || isConfirmed"
            :minDate="form.dateMin"
            :maxDate="form.dateMax"
            :allowedDates="allowedDatesFunction"
            @change="setDate"
          />
          <div v-if="isSubmit && error.errorDate" class="text-error">
            {{ error.errorDate.message }}
          </div>
        </div>
        <div class="col-4">
          <TimeDropdown
            v-model="form.timeSelected"
            :required="true"
            :bookingId="id"
            :serviceId="form.serviceSelected ? form.serviceSelected.id : null"
            :storeCode="form.storeSelected ? form.storeSelected.id : null"
            :date="form.date"
            :setDisable="isReadonly || isConfirmed"
            @setTime="setTime"
            @setTimes="setTimes"
            @setLoading="setTimesLoading"
          ></TimeDropdown>
          <div v-if="isSubmit && error.errorTime" class="text-error">
            {{ error.errorTime.message }}
          </div>
        </div>
        <div class="col-4">
          <PrivilegeDropdown
            v-model="form.privilegeSelected"
            :brandCode="form.brandSelected ? form.brandSelected.id : null"
            :serviceId="form.serviceSelected ? form.serviceSelected.id : null"
            :setDisable="isReadonly || isConfirmed || !isCreatedByAdmin"
            @setPrivilege="setPrivilege"
            @setPrivileges="setPrivileges"
            @setLoading="setPrivilegesLoading"
          ></PrivilegeDropdown>
          <div v-if="isSubmit && error.errorPrivilege" class="text-error">
            {{ error.errorPrivilege.message }}
          </div>
        </div>
        <div class="col-8">
          <VoucherDropdown
            v-model="form.voucherSelected"
            :bookingId="id"
            :serviceId="form.serviceSelected ? form.serviceSelected.id : null"
            :privilegeId="
              form.privilegeSelected ? form.privilegeSelected.id : null
            "
            :storeCode="form.storeSelected ? form.storeSelected.id : null"
            :customerCode="form.customerCode"
            :date="form.date"
            :setDisable="isReadonly || isConfirmed || !isCreatedByAdmin"
            @setVoucher="setVoucher"
            @setVouchers="setVouchers"
            @setLoading="setVouchersLoading"
          ></VoucherDropdown>
          <div v-if="isSubmit && error.errorVoucher" class="text-error">
            {{ error.errorVoucher.message }}
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-4">
          <label for="privilegeTypeInput">Privilege Type</label>
          <input
            id="privilegeTypeInput"
            v-model="form.privilegeType"
            type="text"
            class="form-control"
            style="margin: 0.6rem 0 0"
            readonly
          />
        </div>
        <div class="col-4">
          <label for="voucherNumberInput">Voucher Number</label>
          <input
            id="voucherNumberInput"
            v-model="form.voucherNumber"
            type="text"
            class="form-control"
            style="margin: 0.6rem 0 0"
            readonly
          />
        </div>
      </div>
      <div class="row">
        <div class="col-4">
          <div class="d-flex justify-content-between align-items-center">
            <label for="bookingRoomSelected"
            >Room<span class="required">*</span></label
            >
            <v-progress-circular
              v-if="isSwappableRoomsLoading"
              :size="20"
              :width="2"
              color="secondary"
              indeterminate
            ></v-progress-circular>
            <button
              class="btn btn-danger py-0 px-1 text-caption"
              @click="showSwapRoomPopup"
              v-if="id && !isSwappableRoomsLoading"
              :disabled="selectedSwapStaff != null"
            >
              Switch room
            </button>
          </div>
          <div class="d-flex justify-content-between align-items-center mt-2 gap-2" v-if="selectedSwapRoom">
            <input
              id="switchedRoomInput"
              type="text"
              class="form-control"
              v-model="selectedSwapRoom.roomName"
              readonly
            />
            <button
              class="btn btn-outline-secondary"
              @click="removeSelectedSwapRoom"
            >
              Cancel
            </button>
          </div>

          <RoomDropdown
            v-show="selectedSwapRoom == null"
            v-model="form.roomSelected"
            :isShownLabel="false"
            :required="true"
            :bookingId="id"
            :storeCode="form.storeSelected ? form.storeSelected.id : null"
            :serviceId="form.serviceSelected ? form.serviceSelected.id : null"
            :date="form.date ? form.date : null"
            :time="form.timeSelected ? form.timeSelected.id : null"
            :resourceChecking="true"
            :setDisable="isReadonly || isConfirmed"
            @setRoom="setRoom"
            @setRooms="setRooms"
            @setLoading="setRoomsLoading"
          ></RoomDropdown>
          <div v-if="isSubmit && error.errorRoom" class="text-error">
            {{ error.errorRoom.message }}
          </div>
        </div>
        <div class="col-4">
          <div class="d-flex justify-content-between align-items-center">
            <label for="bookingStaffSelected"
            >Staff<span class="required">*</span></label
            >
            <v-progress-circular
              v-if="isSwappableStaffsLoading"
              :size="20"
              :width="2"
              color="secondary"
              indeterminate
            ></v-progress-circular>
            <button
              class="btn btn-danger py-0 px-1 text-caption"
              @click="showSwapStaffPopup"
              v-if="id && !isSwappableStaffsLoading"
              :disabled="selectedSwapRoom != null"
            >
              Switch staff
            </button>
          </div>
          <div class="d-flex justify-content-between align-items-center mt-2 gap-2" v-if="selectedSwapStaff">
            <input
              id="switchedStaffInput"
              type="text"
              class="form-control"
              v-model="selectedSwapStaff.staffName"
              readonly
            />
            <button
              class="btn btn-outline-secondary"
              @click="removeSelectedSwapStaff"
            >
              Cancel
            </button>
          </div>
          <StaffDropdown
            v-show="selectedSwapStaff == null"
            v-model="form.staffSelected"
            :isShownLabel="false"
            :required="true"
            :bookingId="id"
            :storeCode="form.storeSelected ? form.storeSelected.id : null"
            :serviceId="form.serviceSelected ? form.serviceSelected.id : null"
            :date="form.date ? form.date : null"
            :time="form.timeSelected ? form.timeSelected.id : null"
            :resourceChecking="true"
            :setDisable="isReadonly || isConfirmed"
            @setStaff="setStaff"
            @setStaffs="setStaffs"
            @setLoading="setStaffsLoading"
          ></StaffDropdown>
          <div v-if="isSubmit && error.errorStaff" class="text-error">
            {{ error.errorStaff.message }}
          </div>
        </div>
        <div class="col-4">
          <StatusDropdown
            v-model="form.statusSelected"
            :required="true"
            :setDisable="isReadonly"
            :isConfirmed="isConfirmed"
            @setStatus="setStatus"
            @setStatuses="setStatuses"
          ></StatusDropdown>
          <div v-if="isSubmit && error.errorStatus" class="text-error">
            {{ error.errorStatus.message }}
          </div>
        </div>
        <div class="col-8">
          <label for="remarkInput">Remark</label>
          <input
            id="remark"
            v-model="form.remark"
            type="text"
            class="form-control"
            style="margin: 0.6rem 0 0"
            :readonly="isReadonly"
          />
          <div v-if="isSubmit && error.remark" class="text-error">
            {{ error.remark.message }}
          </div>
        </div>
      </div>
      <div class="d-flex justify-content-end mt-5">
        <router-link
          :to="{ name: 'Booking' }"
          class="btn btn-outline-danger btn-cancel"
          >Back
        </router-link>
        <button
          class="btn btn-outline-success ml-3"
          v-if="!isReadonly"
          @click="onSubmit"
        >
          Save
        </button>
      </div>
    </div>

    <v-dialog v-model="showDialog" width="500" persistent>
      <div class="content-dialog">
        <h3>{{ message }}</h3>
        <div class="row text-center">
          <div v-if="showSuccess && id" class="col-12">
            <button class="btn-back" @click="setDefault">ตกลง</button>
          </div>
          <div v-if="showSuccess" class="col-12">
            <button
              class="btn-back"
              @click="() => $router.push({ name: 'Booking' })"
            >
              กลับสู่หน้าหลัก
            </button>
          </div>
        </div>
        <div v-if="!showSuccess" class="row text-center">
          <div class="col">
            <button class="btn-back" @click="showDialog = false">ปิด</button>
          </div>
        </div>
      </div>
    </v-dialog>
    <v-dialog v-model="isShownSwitchRoomPopup" width="700" persistent>
      <div class="content-dialog">
        <div class="d-flex justify-content-end p-2">
          <button @click="closeSwapRoomPopup">
            <font-awesome-icon :icon="['fa', 'times']"/>
          </button>
        </div>
        <div class="pt-3 pb-2 px-5">
          <div class="text-center">
            <h4>Switch resource</h4>
          </div>
          <div class="pt-3">
            <label for="switchRoomSelected"
            >Room resource</label
            >
            <div class="d-flex" style="margin: 0.6rem 0 0"
            >
              <multiselect
                id="switchRoomSelected"
                v-model="selectedSwapRoom"
                :options="swappableRooms"
                label="roomName"
                track-by="roomId"
              >
              </multiselect>
              <button
                class="btn btn-outline-success ml-3"
                @click="closeSwapRoomPopup"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      </div>
    </v-dialog>
    <v-dialog v-model="isShownSwitchStaffPopup" width="700" persistent>
      <div class="content-dialog">
        <div class="d-flex justify-content-end p-2">
          <button @click="closeSwapStaffPopup">
            <font-awesome-icon :icon="['fa', 'times']"/>
          </button>
        </div>
        <div class="pt-3 pb-2 px-5">
          <div class="text-center">
            <h4>Switch resource</h4>
          </div>
          <div class="pt-3">
            <label for="switchStaffSelected"
            >Staff resource</label
            >
            <div class="d-flex" style="margin: 0.6rem 0 0"
            >
              <multiselect
                id="switchStaffSelected"
                v-model="selectedSwapStaff"
                :options="swappableStaffs"
                label="staffName"
                track-by="staffId"
              >
              </multiselect>
              <button
                class="btn btn-outline-success ml-3"
                @click="closeSwapStaffPopup"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      </div>
    </v-dialog>
  </div>
</template>

<script>
import BrandDropdown from "components/Booking/BrandDropdown";
import StoreDropdown from "components/Booking/StoreDropdown";
import StatusDropdown from "components/Booking/StatusDropdown";
import RoomDropdown from "components/Booking/RoomDropdown";
import StaffDropdown from "components/Booking/StaffDropdown";
import ServiceDropdown from "components/Booking/ServiceDropdown";
import PrivilegeDropdown from "components/Booking/PrivilegeDropdown";
import VoucherDropdown from "components/Booking/VoucherDropdown";
import TimeDropdown from "components/Booking/TimeDropdown";
import DatePicker from "components/Datepicker/Datepicker.vue";
import BookingService from "services/api/booking.service";

export default {
  components: {
    DatePicker,
    BrandDropdown,
    StoreDropdown,
    StatusDropdown,
    RoomDropdown,
    StaffDropdown,
    ServiceDropdown,
    PrivilegeDropdown,
    VoucherDropdown,
    TimeDropdown,
  },
  data() {
    return {
      id: this.$route.query.id ? Number(this.$route.query.id) : null,
      message: null,
      showSuccess: false,
      showDialog: false,
      isLoading: false,
      isCustomerLoading: false,
      isBrandLoading: false,
      isStoreLoading: false,
      isServiceLoading: false,
      isPrivilegeLoading: false,
      isVoucherLoading: false,
      isRoomLoading: false,
      isStaffLoading: false,
      isTimeLoading: false,
      isSubmit: false,
      isReadonly: false,
      isConfirmed: false,
      isCreatedByAdmin: true,
      isShownSwitchRoomPopup: false,
      swappableRooms: [],
      selectedSwapRoom: null,
      isSwappableRoomsLoading: false,
      isShownSwitchStaffPopup: false,
      swappableStaffs: [],
      selectedSwapStaff: null,
      isSwappableStaffsLoading: false,
      enabledDates: [],
      form: {
        brandSelected: null,
        storeSelected: null,
        roomSelected: null,
        staffSelected: null,
        statusSelected: null,
        serviceSelected: null,
        privilegeSelected: null,
        voucherSelected: null,
        storeCode: null,
        privilegeType: null,
        voucherNumber: null,
        customerCode: null,
        customerMobile: null,
        customerName: null,
        date: null,
        dateMin: "1900-01-01",
        dateMax: "1900-01-01",
        timeSelected: null,
        remark: null,
      },
      error: {
        errorBrand: "",
        errorStore: "",
        errorRoom: "",
        errorStaff: "",
        errorStatus: "",
        errorService: "",
        errorPrivilege: "",
        errorVoucher: "",
        errorCustomerCode: "",
        errorCustomerMobile: "",
        errorCustomerName: "",
        errorDate: "",
        errorTime: "",
      },
    };
  },
  async mounted() {
    if (this.id) {
      this.isLoading = true;
      await this.getBookingDetail(this.id);
    }
  },
  computed: {
    showLoading() {
      return (
        this.isLoading ||
        this.isCustomerLoading ||
        this.isBrandLoading ||
        this.isStoreLoading ||
        this.isServiceLoading ||
        this.isTimeLoading ||
        this.isPrivilegeLoading ||
        this.isVoucherLoading ||
        this.isRoomLoading ||
        this.isStaffLoading
      );
    },
    allowedDatesFunction() {
      if (!this.enabledDates || this.enabledDates.length === 0) {
        return null;
      }
      return (date) => this.enabledDates.includes(date);
    },
  },
  methods: {
    onSubmit() {
      this.isSubmit = true;
      this.isLoading = true;
      this.resetErrorText();
      const form = this.form;
      const body = {
        brandCode: form.brandSelected?.id,
        storeCode: form.storeSelected?.id,
        customerMobile: form.customerMobile,
        customerName: form.customerName,
        customerCode: form.customerCode,
        serviceId: form.serviceSelected?.id,
        bookingDate: form.date,
        bookingTime: form.timeSelected?.id,
        bookingStatus: form.statusSelected?.id,
        voucherId: form.voucherSelected?.id,
        roomId: this.selectedSwapRoom ? this.selectedSwapRoom.roomId : form.roomSelected?.id,
        staffId: this.selectedSwapStaff? this.selectedSwapStaff.staffId: form.staffSelected?.id,
        remark: form.remark,
      };
      if (this.id) {
        if (this.selectedSwapRoom) {
          body.swapBookingId = this.selectedSwapRoom.bookingId;
        }
        if (this.selectedSwapStaff) {
          body.swapBookingId = this.selectedSwapStaff.bookingId;
        }
        this.updateExistingBooking(body);
      } else {
        this.createNewBooking(body);
      }
    },
    createNewBooking(body) {
      BookingService.createBooking(body)
        .then((res) => {
          this.createOrUpdateSuccess(res);
        })
        .catch((err) => {
          this.createOrUpdateFailure(err);
        });
    },
    updateExistingBooking(body) {
      BookingService.updateBooking(this.id, body)
        .then((res) => {
          this.createOrUpdateSuccess(res);
        })
        .catch((err) => {
          this.createOrUpdateFailure(err);
        });
    },
    createOrUpdateSuccess(res) {
      this.isLoading = false;
      this.isReadonly =
        this.form.statusSelected && this.form.statusSelected.id === "CANCELLED";
      this.isConfirmed =
        this.form.statusSelected && this.form.statusSelected.id === "USED";
      const data = res.data;
      this.isCreatedByAdmin = data.isCreatedByAdmin;
      this.showDialog = true;
      this.showSuccess = true;
      this.message = data.message ? data.message : "Save booking success";
    },
    createOrUpdateFailure(err) {
      this.isLoading = false;
      const errorData = err.response?.data;
      if (errorData.errors?.length > 0) {
        this.setErrorText(errorData.errors);
        return;
      }
      if (errorData.message) {
        this.showDialog = true;
        this.message = errorData.message;
      }
    },
    async getBookingDetail() {
      await BookingService.getBooking(this.id)
        .then((res) => {
          this.isLoading = false;
          const data = res.data;
          const form = this.form;
          this.isReadonly = data.bookingStatus === "CANCELLED";
          this.isConfirmed = data.bookingStatus === "USED";
          this.isCreatedByAdmin = data.isCreatedByAdmin ?? true;
          form.customerMobile = data.customerMobile;
          form.customerName = data.customerName;
          form.customerCode = data.customerCode;
          form.storeCode = data.storeCode;
          form.date = data.bookingDate;
          form.privilegeType = data.privilegeType;
          form.voucherNumber = data.voucherNumber;
          form.brandSelected = data.brandCode
            ? { id: data.brandCode, text: data.brandName }
            : null;
          form.storeSelected = data.storeCode
            ? { id: data.storeCode, text: data.storeName }
            : null;
          form.timeSelected = data.bookingTime
            ? { id: data.bookingTime, text: data.bookingTime }
            : null;
          form.statusSelected = data.bookingStatus
            ? {
                id: data.bookingStatus,
                text: data.bookingStatus,
              }
            : null;
          form.privilegeSelected = data.privilegeId
            ? {
                id: data.privilegeId,
                text: data.privilegeName,
                detail: data.privilegeType,
              }
            : null;
          form.serviceSelected = data.serviceId
            ? { id: data.serviceId, text: data.serviceName }
            : null;
          form.voucherSelected = data.voucherId
            ? {
                id: data.voucherId,
                text: data.voucherName,
                no: data.voucherNumber,
                expiry: data.voucherExpiry,
              }
            : null;
          form.roomSelected = data.roomId
            ? { id: data.roomId, text: data.roomName }
            : null;
          form.staffSelected = data.staffId
            ? { id: data.staffId, text: data.staffName }
            : null;
          form.remark = data.remark;
          if (form.serviceSelected && form.storeSelected) {
            this.loadAvailableDates(form.serviceSelected.id, form.storeSelected.id);
          }
        })
        .catch((err) => {
          this.createOrUpdateFailure(err);
        });
    },
    async setDefault() {
      this.showDialog = false;
      this.isSubmit = false;
    },
    async setBrand(brand) {
      if (
        this.form.brandSelected &&
        brand &&
        this.form.brandSelected.id === brand.id
      )
        return;
      this.form.brandSelected = brand || null;
      this.form.storeSelected = null;
      this.form.roomSelected = null;
      this.form.staffSelected = null;
      this.form.serviceSelected = null;
      this.form.privilegeSelected = null;
      this.form.voucherSelected = null;
      this.form.timeSelected = null;
      this.form.date = null;
      this.setNoSelectableDates();
    },
    async reloadDates(selectedService) {
      this.form.date = null;
      this.setNoSelectableDates();

      if (selectedService && selectedService.id && this.form.storeSelected?.id) {
        await this.loadAvailableDates(selectedService.id, this.form.storeSelected.id);
      }
    },
    async loadAvailableDates(serviceId, storeCode) {
      try {
        const response = await BookingService.getDates({
          serviceId: serviceId,
          storeCode: storeCode,
          bookingId: this.id
        });

        const dates = response.data.items;

        if (dates && dates.length > 0) {
          this.enabledDates = dates.map(item => item.value);

          const sortedDates = [...this.enabledDates].sort();

          this.form.dateMin = sortedDates[0];
          this.form.dateMax = sortedDates[sortedDates.length - 1];
        } else {
          this.setNoSelectableDates();
          this.enabledDates = [];
        }
      } catch (error) {
        console.log("Error loading dates:", error);
        this.form.dateMin = null;
        this.form.dateMax = null;
        this.enabledDates = [];
      }
    },

    setNoSelectableDates() {
      this.form.dateMin = "1900-01-01";
      this.form.dateMax = "1900-01-01";
      this.enabledDates = [];
    },
    setBrands(brands) {
      brands.forEach((brand) => {
        if (brands.length === 1 || brand.id === this.form.brandSelected) {
          this.form.brandSelected = brand;
        }
      });
    },
    setBrandsLoading(isLoading) {
      this.isBrandLoading = isLoading;
    },
    setStore(store) {
      if (
        this.form.storeSelected &&
        store &&
        this.form.storeSelected.id === store.id
      )
        return;
      this.form.storeCode = store?.id;
      this.form.storeSelected = store || null;
      this.form.roomSelected = null;
      this.form.staffSelected = null;
      this.form.serviceSelected = null;
      this.form.privilegeSelected = null;
      this.form.voucherSelected = null;
      this.form.timeSelected = null;
      this.form.date = null
      this.setNoSelectableDates();
    },
    setStores(stores) {
      stores.forEach((store) => {
        if (stores.length === 1 || store.id === this.form.storeSelected) {
          this.form.storeSelected = store;
          this.form.storeCode = store.id;
        }
      });
    },
    setStoresLoading(isLoading) {
      this.isStoreLoading = isLoading;
    },
    setService(service) {
      if (
        this.form.serviceSelected &&
        service &&
        this.form.serviceSelected.id === service.id
      )
        return;
      this.form.serviceSelected = service || null;
      this.form.roomSelected = null;
      this.form.staffSelected = null;
      this.form.privilegeSelected = null;
      this.form.voucherSelected = null;
      this.form.timeSelected = null;

      this.reloadDates(service);
    },
    setServices(services) {
      services.forEach((service) => {
        if (service.id === this.form.serviceSelected) {
          this.form.serviceSelected = service;
        }
      });
    },
    setServicesLoading(isLoading) {
      this.isServiceLoading = isLoading;
    },
    setPrivilege(privilege) {
      if (
        this.form.privilegeSelected &&
        privilege &&
        this.form.privilegeSelected.id === privilege.id
      )
        return;
      this.form.privilegeType = privilege?.detail;
      this.form.privilegeSelected = privilege || null;
      this.form.voucherSelected = null;
    },
    setPrivileges(privileges) {
      privileges.forEach((privilege) => {
        if (privilege.id === this.form.privilegeSelected) {
          this.form.privilegeSelected = privilege;
        }
      });
    },
    setPrivilegesLoading(isLoading) {
      this.isPrivilegeLoading = isLoading;
    },
    setVoucher(voucher) {
      this.form.voucherNumber = voucher?.no;
      this.form.voucherSelected = voucher || null;
      if (voucher) {
        this.form.dateMax = voucher.expiry;
      } else {
        this.form.dateMax = null;
      }
    },
    setVouchers(vouchers) {
      vouchers.forEach((voucher) => {
        if (voucher.id === this.form.voucherSelected) {
          this.form.voucherSelected = voucher;
        }
      });
    },
    setVouchersLoading(isLoading) {
      this.isVoucherLoading = isLoading;
    },
    setDate(date) {
      if (this.form.date && date && this.form.date === date) return;
      this.form.date = date;
      this.form.timeSelected = null;
      this.form.roomSelected = null;
      this.form.staffSelected = null;
    },
    setTime(time) {
      if (
        this.form.timeSelected &&
        time &&
        this.form.timeSelected.id === time.id
      )
        return;
      this.form.timeSelected = time || null;
      this.form.roomSelected = null;
      this.form.staffSelected = null;
    },
    setTimes(times) {
      times.forEach((time) => {
        if (time.id === this.form.timeSelected) {
          this.form.timeSelected = time;
        }
      });
    },
    setTimesLoading(isLoading) {
      this.isTimeLoading = isLoading;
    },
    setRoom(room) {
      this.form.roomSelected = room || null;
    },
    setRooms(rooms) {
      rooms.forEach((room) => {
        if (room.id === this.form.roomSelected) {
          this.form.roomSelected = room;
        }
      });
    },
    setRoomsLoading(isLoading) {
      this.isRoomLoading = isLoading;
    },
    setStaff(staff) {
      this.form.staffSelected = staff || null;
    },
    setStaffs(staffs) {
      staffs.forEach((staff) => {
        if (staff.id === this.form.staffSelected) {
          this.form.staffSelected = staff;
        }
      });
    },
    setStaffsLoading(isLoading) {
      this.isStaffLoading = isLoading;
    },
    setStatus(status) {
      this.form.statusSelected = status || null;
    },
    setStatuses(statuses) {
      statuses.forEach((status) => {
        if (status.id === this.form.statusSelected) {
          this.form.statusSelected = status;
        }
      });
    },
    async setCustomerMobile() {
      this.isCustomerLoading = true;
      await BookingService.getCustomer({ mobile: this.form.customerMobile })
        .then((res) => {
          const data = res.data;
          if (this.form.customerCode === data.code) return;
          if (
            this.form.customerName === null ||
            this.form.customerName === ""
          ) {
            this.form.customerName = data.name;
          }
          this.form.customerCode = data.code;
          this.form.voucherSelected = null;
        })
        .catch((err) => {
          this.form.customerCode = null;
          this.form.voucherSelected = null;
          console.log(err);
        });
      this.isCustomerLoading = false;
    },
    async getSwappableRooms() {
      try {
        this.isSwappableRoomsLoading = true;
        const params = {
          bookingId: this.id,
        };
        await BookingService.getSwappableRooms(params)
            .then(async (res) => {
              this.swappableRooms =  await res.data.slots;
            })
            .catch((error) => {
              console.log("Error: ", error);
            });
      } catch (error) {
        console.log("Error: ", error);
      }
      this.isSwappableRoomsLoading = false;
    },
    async getSwappableStaffs() {
      try {
        this.isSwappableStaffsLoading = true;
        const params = {
          bookingId: this.id,
        };
        await BookingService.getSwappableStaffs(params)
          .then(async (res) => {
            this.swappableStaffs = await res.data.slots;
          })
          .catch((error) => {
            console.log("Error: ", error);
          });
      } catch (error) {
        console.log("Error: ", error);
      }
      this.isSwappableStaffsLoading = false;
    },
    async showSwapRoomPopup() {
      await this.getSwappableRooms();
      this.isShownSwitchRoomPopup = true;
    },
    closeSwapRoomPopup() {
      this.isShownSwitchRoomPopup = false;
    },
    removeSelectedSwapRoom() {
      this.selectedSwapRoom = null;
    },
    async showSwapStaffPopup() {
      await this.getSwappableStaffs();
      this.isShownSwitchStaffPopup = true;
    },
    closeSwapStaffPopup() {
      this.isShownSwitchStaffPopup = false;
    },
    removeSelectedSwapStaff() {
      this.selectedSwapStaff = null;
    },
    setErrorText(errors) {
      this.error.errorBrand = errors.find((e) => e.fieldName === "brandCode");
      this.error.errorStore = errors.find((e) => e.fieldName === "storeCode");
      this.error.errorRoom = errors.find((e) => e.fieldName === "roomId");
      this.error.errorStaff = errors.find((e) => e.fieldName === "staffId");
      this.error.errorService = errors.find((e) => e.fieldName === "serviceId");
      this.error.errorVoucher = errors.find((e) => e.fieldName === "voucherId");
      this.error.errorDate = errors.find((e) => e.fieldName === "bookingDate");
      this.error.errorTime = errors.find((e) => e.fieldName === "bookingTime");
      this.error.errorStatus = errors.find(
        (e) => e.fieldName === "bookingStatus"
      );
      this.error.errorPrivilege = errors.find(
        (e) => e.fieldName === "privilegeId"
      );
      this.error.errorCustomerCode = errors.find(
        (e) => e.fieldName === "customerCode"
      );
      this.error.errorCustomerMobile = errors.find(
        (e) => e.fieldName === "customerMobile"
      );
      this.error.errorCustomerName = errors.find(
        (e) => e.fieldName === "customerName"
      );
    },
    resetErrorText() {
      this.error = {
        errorBrand: "",
        errorStore: "",
        errorRoom: "",
        errorStaff: "",
        errorStatus: "",
        errorService: "",
        errorPrivilege: "",
        errorVoucher: "",
        errorCustomerCode: "",
        errorCustomerMobile: "",
        errorCustomerName: "",
        errorDate: "",
        errorTime: "",
      };
    },
  },
};
</script>

<style lang="scss" scoped>
.btn-cancel {
  color: red;
}

.btn-cancel:hover {
  color: white;
}

.booking-loading > :not(:first-child) {
  pointer-events: none;
  opacity: 0.4;
}

::v-deep .multiselect--disabled {
  opacity: 1;
}

::v-deep .multiselect--disabled > .multiselect__tags {
  background-color: #e9ecef;
}

::v-deep .multiselect--disabled > .multiselect__tags > .multiselect__single {
  background-color: #e9ecef;
}
</style>
